import { apiService } from './apiService';

export const getTestCycles = async (projectId: number) => {
  const response = await apiService.get(`/projects/${projectId}/test-cycles`);
  return response.data;
};

export const checkDuplicateName = async (projectId: number, name: string, excludeCycleId?: number): Promise<boolean> => {
  const cycles = await getTestCycles(projectId);
  const normalized = name.trim().toLowerCase();
  return cycles.some((c: { id: number; name: string }) => {
    if (excludeCycleId != null && c.id === excludeCycleId) return false;
    return (c.name || '').trim().toLowerCase() === normalized;
  });
};

const testCycleDetailsCache = new Map<string, { data: unknown; timestamp: number }>();
const testCycleDetailsInflight = new Map<string, Promise<unknown>>();
const TEST_CYCLE_DETAILS_CACHE_TTL_MS = 60_000;

const testCycleDetailsCacheKey = (projectId: number, cycleId: number) => `${projectId}:${cycleId}`;

export const getCachedTestCycleDetails = (projectId: number, cycleId: number): any | null => {
  const entry = testCycleDetailsCache.get(testCycleDetailsCacheKey(projectId, cycleId));
  if (!entry) return null;
  if (Date.now() - entry.timestamp > TEST_CYCLE_DETAILS_CACHE_TTL_MS) {
    testCycleDetailsCache.delete(testCycleDetailsCacheKey(projectId, cycleId));
    return null;
  }
  return entry.data;
};

/** Drop cached test plan details so the next view/edit loads fresh collections. */
export const invalidateTestCycleDetailsCache = (projectId: number, cycleId: number) => {
  const key = testCycleDetailsCacheKey(projectId, cycleId);
  testCycleDetailsCache.delete(key);
  testCycleDetailsInflight.delete(key);
};

/** Clear all cached test plan details (e.g. after Collections data-set create/delete). */
export const invalidateAllTestCycleDetailsCache = () => {
  testCycleDetailsCache.clear();
  testCycleDetailsInflight.clear();
};

/** Fired when Collections data sets change so an open Test Plan can refetch. */
export const TEST_PLAN_DATASETS_UPDATED_EVENT = 'test-plan-datasets-updated';

/** Start loading test plan details before navigation so the details page opens immediately. */
export const prefetchTestCycleDetails = (projectId: number, cycleId: number) => {
  void getTestCycleDetails(projectId, cycleId);
};

export const getTestCycleDetails = async (
  projectId: number,
  cycleId: number,
  options?: { skipCache?: boolean }
) => {
  const key = testCycleDetailsCacheKey(projectId, cycleId);
  if (options?.skipCache) {
    invalidateTestCycleDetailsCache(projectId, cycleId);
  }
  const cached = getCachedTestCycleDetails(projectId, cycleId);
  if (cached) return cached;

  const inflight = testCycleDetailsInflight.get(key);
  if (inflight) return inflight;

  const promise = apiService
    .get(`/projects/${projectId}/test-cycles/${cycleId}`)
    .then((response) => {
      testCycleDetailsCache.set(key, { data: response.data, timestamp: Date.now() });
      testCycleDetailsInflight.delete(key);
      return response.data;
    })
    .catch((err) => {
      testCycleDetailsInflight.delete(key);
      throw err;
    });

  testCycleDetailsInflight.set(key, promise);
  return promise;
};

export const createTestCycle = async (projectId: number, data: any) => {
  const response = await apiService.post(`/projects/${projectId}/test-cycles`, data);
  return response.data;
};

export const updateTestCycle = async (projectId: number, cycleId: number, data: any) => {
  const response = await apiService.put(`/projects/${projectId}/test-cycles/${cycleId}`, data);
  return response.data;
};

export const deleteTestCycle = async (projectId: number, cycleId: number) => {
  const response = await apiService.delete(`/projects/${projectId}/test-cycles/${cycleId}`);
  return response.data;
};

export const saveTestCycleConfig = async (
  cycleId: number,
  payload: {
    collectionIds: number[];
    collectionToRequestIds: Record<number, number[]>;
    requestToDataSetIds: Record<number, number[]>;
    dataSetToAssertionIds?: Record<number, number[]>;
  }
) => {
  const response = await apiService.post(`/test-cycles/${cycleId}/config/bulk`, payload);
  return response.data;
};

export const setCollections = async (cycleId: number, collectionIds: number[]) => {
  const response = await apiService.post(`/test-cycles/${cycleId}/config/collections`, collectionIds);
  return response.data;
};

export const setRequests = async (cycleId: number, mapping: Record<number, number[]>) => {
  const response = await apiService.post(`/test-cycles/${cycleId}/config/requests`, mapping);
  return response.data;
};

export const setDataSets = async (cycleId: number, mapping: Record<number, number[]>) => {
  const response = await apiService.post(`/test-cycles/${cycleId}/config/data-sets`, mapping);
  return response.data;
};

export const setAssertions = async (cycleId: number, mapping: Record<number, number[]>) => {
  const response = await apiService.post(`/test-cycles/${cycleId}/config/assertions`, mapping);
  return response.data;
};

export const startTestCycleRun = async (
  cycleId: number,
  environmentId: number,
  envVariables?: Record<string, string>,
  environmentUrl?: string | null,
  options?: { clientDriven?: boolean }
) => {
  const body: {
    environmentId: number;
    envVariables?: Record<string, string>;
    environmentUrl?: string;
    clientDriven?: boolean;
  } = { environmentId, envVariables };
  if (environmentUrl != null && environmentUrl.trim() !== '') body.environmentUrl = environmentUrl.trim();
  // Frontend proxy execution (incl. round-robin): create run shell only — do not start
  // server-side request-by-request execution in parallel.
  if (options?.clientDriven) body.clientDriven = true;
  const response = await apiService.post(`/test-cycles/${cycleId}/runs`, body);
  return response.data;
};

export type TestCycleRunRetentionInfo = {
  retentionDays: number;
  historyStart: string;
  historyEnd: string;
  daysLeft: number;
};

export type TestCycleRunsResponse = {
  runs: any[];
  retention?: TestCycleRunRetentionInfo;
};

const normalizeTestCycleRunsResponse = (data: unknown): TestCycleRunsResponse => {
  if (Array.isArray(data)) {
    return { runs: data };
  }
  if (data && typeof data === 'object' && Array.isArray((data as TestCycleRunsResponse).runs)) {
    return data as TestCycleRunsResponse;
  }
  return { runs: [] };
};

export const getTestCycleRuns = async (cycleId: number): Promise<TestCycleRunsResponse> => {
  const response = await apiService.get(`/test-cycles/${cycleId}/runs`);
  return normalizeTestCycleRunsResponse(response.data);
};

function positiveSnapshotId(value: unknown): number | null {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Persisted run rows keep request/data-set identity on snapshot columns after the
 * live FK is cleared. Another browser has no local complete report, so those
 * columns are what History uses to tell an executed step from Not Executed.
 * Rows that already include request.id / dataSet.id are returned unchanged.
 */
export function normalizeTestCycleRunResultRow<T>(raw: T): T {
  if (!raw || typeof raw !== 'object') return raw;
  const row = raw as Record<string, any>;
  const requestId =
    positiveSnapshotId(row.request?.id) ??
    positiveSnapshotId(row.requestSnapshotId) ??
    positiveSnapshotId(row.requestId);
  const dataSetId =
    positiveSnapshotId(row.dataSet?.id) ??
    positiveSnapshotId(row.dataSetSnapshotId) ??
    positiveSnapshotId(row.dataSetId);
  const existingRequestId = positiveSnapshotId(row.request?.id);
  const existingDataSetId = positiveSnapshotId(row.dataSet?.id);
  if (existingRequestId != null && (dataSetId == null || existingDataSetId === dataSetId)) {
    return raw;
  }
  const collectionId =
    positiveSnapshotId(row.request?.collectionId) ??
    positiveSnapshotId(row.request?.collection?.id) ??
    positiveSnapshotId(row.collectionSnapshotId);
  const request =
    requestId == null
      ? row.request
      : {
          ...(row.request && typeof row.request === 'object' ? row.request : {}),
          id: requestId,
          name:
            (row.request && typeof row.request === 'object' ? row.request.name : undefined) ??
            row.requestNameSnapshot,
          method:
            (row.request && typeof row.request === 'object' ? row.request.method : undefined) ??
            row.requestMethodSnapshot,
          ...(collectionId != null ? { collectionId } : {}),
          ...(row.collectionNameSnapshot ? { collectionName: row.request?.collectionName ?? row.collectionNameSnapshot } : {}),
        };
  const dataSet =
    dataSetId == null
      ? row.dataSet
      : {
          ...(row.dataSet && typeof row.dataSet === 'object' ? row.dataSet : {}),
          id: dataSetId,
          name:
            (row.dataSet && typeof row.dataSet === 'object' ? row.dataSet.name : undefined) ??
            row.dataSetName,
        };
  return { ...row, request, dataSet } as T;
}

export const getTestCycleRunResults = async (cycleId: number, runId: number) => {
  const response = await apiService.get(`/test-cycles/${cycleId}/runs/${runId}/results`);
  const data = response.data;
  if (!Array.isArray(data)) return data;
  return data.map((row) => normalizeTestCycleRunResultRow(row));
};

const COMPLETE_REPORT_TIMEOUT_MS = 120_000;

export type TestCycleCompleteReportDocument = {
  schemaVersion: number;
  cycleId: number;
  runId: number;
  rowCount?: number;
  rows: import('../components/CompleteExecutionResultsPanel').CompleteExecutionReportResultRow[];
};

export type TestCycleCompleteReportFetch =
  | { kind: 'found'; schemaVersion: number; cycleId: number; runId: number; rows: TestCycleCompleteReportDocument['rows'] }
  | { kind: 'not_found' }
  | { kind: 'error' };

export type TestCycleCompleteReportSave =
  | { kind: 'saved' }
  | { kind: 'conflict' }
  | { kind: 'error'; message: string };

const reportErrorCode = (error: any): string => {
  const data = error?.response?.data;
  return typeof data?.code === 'string' ? data.code : '';
};

const reportErrorStatus = (error: any): number | undefined => {
  const status = error?.response?.status;
  return typeof status === 'number' ? status : undefined;
};

/** Load the server copy of a complete execution report. Only REPORT_NOT_FOUND means it is absent. */
export const getTestCycleCompleteReport = async (
  cycleId: number,
  runId: number
): Promise<TestCycleCompleteReportFetch> => {
  try {
    const response = await apiService.get(`/test-cycles/${cycleId}/runs/${runId}/complete-report`, {
      timeout: COMPLETE_REPORT_TIMEOUT_MS,
    });
    const data = response?.data;
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    return {
      kind: 'found',
      schemaVersion: Number(data?.schemaVersion) || 0,
      cycleId: Number(data?.cycleId),
      runId: Number(data?.runId),
      rows,
    };
  } catch (error: any) {
    if (reportErrorCode(error) === 'REPORT_NOT_FOUND') return { kind: 'not_found' };
    return { kind: 'error' };
  }
};

/** Idempotent save of the complete report for an existing run. Does not create a run. */
export const saveTestCycleCompleteReport = async (
  cycleId: number,
  runId: number,
  rows: TestCycleCompleteReportDocument['rows']
): Promise<TestCycleCompleteReportSave> => {
  try {
    await apiService.put(
      `/test-cycles/${cycleId}/runs/${runId}/complete-report`,
      {
        schemaVersion: 1,
        cycleId,
        runId,
        rows,
      },
      { timeout: COMPLETE_REPORT_TIMEOUT_MS }
    );
    return { kind: 'saved' };
  } catch (error: any) {
    const status = reportErrorStatus(error);
    const code = reportErrorCode(error);
    if (status === 409 || code === 'REPORT_INCOMPLETE') return { kind: 'conflict' };
    if (status === 413 || code === 'REPORT_TOO_LARGE') {
      return { kind: 'error', message: 'The detailed report is too large to store on the server.' };
    }
    return { kind: 'error', message: 'Failed to save the detailed report to the server.' };
  }
};

export const completeTestCycleRun = async (cycleId: number, runId: number) => {
  const response = await apiService.post(`/test-cycles/${cycleId}/runs/${runId}/complete`);
  return response.data;
};

export const executeCollection = async (
  cycleId: number,
  collectionId: number,
  environmentId: number,
  environmentUrl?: string | null
) => {
  const body: { environmentId: number; environmentUrl?: string } = { environmentId };
  if (environmentUrl != null && environmentUrl.trim() !== '') body.environmentUrl = environmentUrl.trim();
  const response = await apiService.post(`/test-cycles/${cycleId}/runs/collection/${collectionId}`, body);
  return response.data;
};

export const executeRequest = async (
  cycleId: number,
  requestId: number,
  environmentId: number,
  environmentUrl?: string | null
) => {
  const body: { environmentId: number; environmentUrl?: string } = { environmentId };
  if (environmentUrl != null && environmentUrl.trim() !== '') body.environmentUrl = environmentUrl.trim();
  const response = await apiService.post(`/test-cycles/${cycleId}/runs/request/${requestId}`, body);
  return response.data;
};

export const executeDataSet = async (cycleId: number, dataSetId: number, environmentId: number) => {
  const response = await apiService.post(`/test-cycles/${cycleId}/runs/data-set/${dataSetId}`, { environmentId });
  return response.data;
};

export interface TestCycleReportPayload {
  to: string[];
  cc: string[];
  /** Base64-encoded PDF (same as Download PDF) so email attachment matches downloaded file */
  pdfBase64?: string;
  /** Suggested attachment filename when using pdfBase64 */
  pdfFileName?: string;
  report: {
    cycleName: string;
    requestPassRate: string;
    requests: { total: number; passed: number; failed: number; notExecuted: number };
    dataSets: { total: number; passed: number; failed: number; notExecuted: number };
    assertions: { total: number; passed: number; failed: number; notExecuted: number };
  };
}

export const sendTestCycleReport = async (projectId: number, cycleId: number, payload: TestCycleReportPayload) => {
  const response = await apiService.post(`/projects/${projectId}/test-cycles/${cycleId}/send-report`, payload);
  return response.data;
}; 